import { InventoryItemFragment } from '@epicuro-next/data/product';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  selectAdvancedMDOAvailableAndEnabled,
  selectShowWholeSalePrices,
} from '@epicuro-next/state/user-state';
import {
  firstDayOfMonthDate,
  firstDayOfMonthUTC,
  firstDayOfNextWeek,
  firstDayOfNextWeekUTC,
  firstDayOfWeekUTC,
  getDeliveryShopId,
  isBefore,
  isInDateRange,
  isNonNullish,
  memoize,
  Unpacked,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { DeliveryWindowType, OrderType } from 'graphql-api/gql-api-interfaces';
import { DateTime } from 'luxon';

import {
  ActiveProductPricesDic,
  CachedState,
  DEFAULT_DELIVERY_PERIOD_ID,
  DeliveryItem,
  DeliveryItems,
  DeliveryItemsByPeriod,
  DeliveryPeriodType,
  DisplayByMode,
  MemoProductSizes,
  ProductVariant,
} from '../models';
import { compareVariants } from '../shared/compare-variants';
import { getShopSelectedDeliveryPeriod } from '../shared/get-selected-delivery-period';
import { inventoryFilter } from '../shared/inventory-filter';
import { isNeverAvailableVariant } from '../shared/is-never-available-variant';
import { isVariantAvailable } from '../shared/is-variant-avaiilable';
import { mapBulkDeliveryPeriodToFuture } from '../shared/map-bulk-delivery-to-future';
import {
  getInventoriesFilteredByShopPeriod,
  getPriceType,
  getProductPrices,
} from '../shared/shared';
import { isTrueDemandInventory } from '../shared/true-demand-inventory';

import {
  selectBusinessConfigStripState,
  selectCachedState,
  selectCurrentlyActiveDeliveryShopKey,
  selectDeliveryItems,
  selectDeliveryPeriods,
  selectDirty,
  selectPersistingChanges,
  selectTotalPrices,
  selectTotalQuantity,
  selectUsedInventories,
} from './business/config-strip-business.selectors';
import { selectSplitInventoriesOverviewForSameDateEnabled } from './config-strip-configuration.selectors';
import {
  selectDataConfigStripState,
  selectDeliveryShops,
  selectDeliveryShopsCount,
  selectDeliveryWindowType,
  selectPrepackInventories,
  selectPrepacks,
  selectPrices,
  selectProduct,
  selectProductId,
  selectSizeEntities,
  selectSizes,
  selectSizesInventories,
  selectVariantEntities,
  selectVariants,
} from './data/config-strip.data.selectors';
import { isMonthType, isWeekType } from './delivery-periods/utils';
import {
  countShopDeliveryItemTotals,
  countShopDeliveryItemTrueDemandTotals,
} from './shared/count-items-totals';
import {
  availableInventoriesForSelectedSizeForOrderType,
  availableInventoriesQuantitiesForDeliveryPeriod,
} from './shared/inventory-overview';
import {
  selectAdvancedMdo,
  selectCurrentlyActiveOrderType,
  selectCurrentlySelectedPrepackId,
  selectCurrentlySelectedSizeId,
  selectCurrentlySelectedVariantId,
  selectDisplayByMode,
  selectInitialSelectedVariantId,
  selectIsDisabledOnBasketUpdate,
  selectIsUpdatingCart,
  selectValidating,
} from './ui/config-strip.ui.selectors';

interface IKeyValueOfType<T> {
  [key: string]: T;
}

/** Select currently selected delivery periods for current shop */
export const selectCurrentShopDeliveryPeriods = createSelector(
  selectDeliveryPeriods,
  selectCurrentlyActiveDeliveryShopKey,
  (deliveryPeriods, shopId) => deliveryPeriods[shopId] ?? {},
);

/** Select currently selected delivery period type for current shop */
export const selectCurrentShopCurrentDeliveryPeriodType = createSelector(
  selectCurrentShopDeliveryPeriods,
  (shopDeliveryPeriods) => shopDeliveryPeriods.currentDeliveryPeriodType,
);

// TODO: Has to return Date, because everything goes out from store as Date object
export const selectCurrentlyActiveDeliveryPeriod = createSelector(
  selectCurrentShopDeliveryPeriods,
  selectCurrentShopCurrentDeliveryPeriodType,
  (currentShopDeliveryPeriods, currentShopCurrentDeliveryPeriodType) => {
    return getShopSelectedDeliveryPeriod(
      currentShopDeliveryPeriods,
      currentShopCurrentDeliveryPeriodType,
    );
  },
);

export const selectDeliveryItemsAcrossShops = createSelector(
  selectDeliveryItems,
  (deliveryItems) =>
    Object.values(deliveryItems).reduce((acc, curr) => {
      Object.entries(curr).forEach(([deliveryPeriod, data]) => {
        const { items, deliveryPeriodType } = data;
        if (acc[deliveryPeriod]) {
          Object.entries(items).forEach(([itemId, itemData]) => {
            if (acc[deliveryPeriod].items[itemId]) {
              const oldItem = { ...acc[deliveryPeriod].items[itemId] };
              oldItem.quantity += itemData.quantity;
              acc[deliveryPeriod].deliveryPeriodType = deliveryPeriodType;
              acc[deliveryPeriod].items = {
                ...acc[deliveryPeriod].items,
                [itemId]: oldItem,
              };
            } else {
              acc[deliveryPeriod].deliveryPeriodType = deliveryPeriodType;
              acc[deliveryPeriod].items = {
                ...acc[deliveryPeriod].items,
                [itemId]: { ...itemData },
              };
            }
          });
        } else {
          acc[deliveryPeriod] = { ...data };
        }
      });
      return acc;
    }, {}),
);

export const selectCurrentlyActiveShopDeliveryItems = createSelector(
  selectDeliveryItems,
  selectCurrentlyActiveDeliveryShopKey,
  (deliveryItems, deliveryShop) => {
    return deliveryItems ? deliveryItems[deliveryShop] ?? {} : {};
  },
);

export const selectIsPasteIconAvailable = createSelector(
  selectCurrentlyActiveShopDeliveryItems,
  selectCurrentlyActiveDeliveryPeriod,
  selectDeliveryItemsAcrossShops,
  selectAdvancedMdo,
  (
    deliveryItems: DeliveryItemsByPeriod,
    currentlyActiveDeliveryPeriod: string,
    itemsAcrossShops,
    isAdvancedMdo,
    deliveryPeriod: string = DeliveryPeriodType.Now,
  ) => {
    const items = isAdvancedMdo ? itemsAcrossShops : deliveryItems;
    return (
      currentlyActiveDeliveryPeriod !== deliveryPeriod &&
      Object.values(items[currentlyActiveDeliveryPeriod]?.items ?? {}).some(
        ({ quantity }) => quantity > 0,
      )
    );
  },
);

/** Return current delivery items taking into account delivery type and shop */
export const selectCurrentlyActiveDeliveryItems = createSelector(
  selectCurrentlyActiveShopDeliveryItems,
  selectCurrentlyActiveDeliveryPeriod,
  selectCurrentShopCurrentDeliveryPeriodType,
  (deliveryItems, deliveryPeriod) => {
    const requestedDeliveryPeriod =
      deliveryPeriod || DEFAULT_DELIVERY_PERIOD_ID;

    let result: DeliveryItems | undefined =
      deliveryItems[requestedDeliveryPeriod];

    if (!result) {
      result = Object.values(deliveryItems).find(
        (item) => item.deliveryPeriodType === requestedDeliveryPeriod,
      );
    }

    return (
      result ??
      ({
        items: {},
      } as DeliveryItems)
    );
  },
);

export const selectShopDeliveryItemTotals = createSelector(
  selectPrepacks,
  selectDeliveryItems,
  countShopDeliveryItemTotals,
);

export const selectShopDeliveryItemTrueDemandTotals = createSelector(
  selectPrepacks,
  selectDeliveryItems,
  countShopDeliveryItemTrueDemandTotals,
);

export const selectDeliveryQuantityForSelectedSizeForSelectedPeriod =
  createSelector(
    selectCurrentlySelectedSizeId,
    selectCurrentlyActiveDeliveryPeriod,
    selectDeliveryItems,
    selectCurrentlyActiveOrderType,
    (sizeId, deliveryPeriod, deliveryItems, orderType) => {
      if (sizeId === null) {
        return 0;
      }

      const deliveryKey =
        deliveryPeriod !== null
          ? deliveryPeriod
          : deliveryPeriod === null && orderType === OrderType.Now
          ? orderType
          : false;

      if (deliveryKey) {
        return Object.values(deliveryItems).reduce((aggr, shopItems) => {
          return aggr + (shopItems[deliveryKey]?.items[sizeId]?.quantity ?? 0);
        }, 0);
      }

      return 0;
    },
  );

export const selectCurrentlySelectedVariant = createSelector(
  selectVariantEntities,
  selectCurrentlySelectedVariantId,
  (variants, selectedVariantId) => {
    if (selectedVariantId === null) {
      return null;
    }

    return variants?.[selectedVariantId];
  },
);

export const selectFirstOrCurrentlySelectedVariant = createSelector(
  selectVariantEntities,
  selectCurrentlySelectedVariantId,
  (variants, selectedVariantId) => {
    const firstVariant = Object.values(variants)[0];
    if (selectedVariantId !== null) {
      return variants[selectedVariantId] || firstVariant;
    }

    return firstVariant;
  },
);

export const selectCurrentVariantDescriptionAttributesWithFallback =
  createSelector(
    selectProduct,
    selectFirstOrCurrentlySelectedVariant,
    (product, variant) =>
      variant?.descriptionAttributes?.length
        ? variant?.descriptionAttributes
        : product?.descriptionAttributes,
  );

export const selectCurrentlySelectedSize = createSelector(
  selectSizeEntities,
  selectCurrentlySelectedSizeId,
  (sizes, selectedSizeId) => {
    if (selectedSizeId === null) {
      return null;
    }

    return sizes[selectedSizeId];
  },
);

export const selectDeliveryItemsMapToShops = createSelector(
  selectBusinessConfigStripState,
  selectCurrentlyActiveDeliveryPeriod,
  (business, activePeriod) =>
    Object.entries(business.deliveryItems).reduce(
      (
        acc: IKeyValueOfType<IKeyValueOfType<DeliveryItem>>,
        [shopKey, quantities],
      ) => {
        const quantityItems = Object.entries(quantities)
          .filter(([period]) => period === activePeriod)
          .reduce(
            (ac: IKeyValueOfType<DeliveryItem>, [_, { items }]) => ({
              ...ac,
              ...items,
            }),
            {},
          );
        return {
          ...acc,
          [shopKey]: {
            ...(acc[shopKey] || {}),
            ...quantityItems,
          },
        };
      },
      {},
    ),
);

/**
 * Returns the available Products of type Sizes that match the currently selected Order Type and delivery period.
 * A Size is considered to be available if it has inventory for the selected Delivery Period and Order Type.
 */
export const selectSizesForCurrentDeliveryPeriod = createSelector(
  selectSizes,
  selectCurrentlyActiveOrderType,
  selectCurrentlyActiveDeliveryPeriod,
  selectCurrentShopCurrentDeliveryPeriodType,
  selectDeliveryWindowType,
  (
    sizes,
    orderType,
    deliveryPeriod,
    currentDeliveryPeriodType,
    deliveryWindow,
  ) => {
    return sizes.filter((size) =>
      size?.inventoryItems?.some(
        inventoryFilter(
          orderType,
          mapBulkDeliveryPeriodToFuture(
            deliveryPeriod,
            currentDeliveryPeriodType,
          ),
          currentDeliveryPeriodType,
          deliveryWindow,
        ),
      ),
    );
  },
);

/**
 * Returns the ids of the available Products of type Sizes that match the currently selected Order Type and delivery period.
 * A Size is considered to be available if it has inventory for the selected Delivery Period and Order Type.
 *
 *
 */
const selectCurrentlyAvailableSizeIds = createSelector(
  selectSizesForCurrentDeliveryPeriod,
  (sizes) => {
    return sizes.map((size) => size?.productId);
  },
);

export const selectUsedInventoriesForOtherDeliveryPeriods = memoize(
  (checkAcrossShops: boolean) =>
    createSelector(
      selectUsedInventories,
      selectCurrentlyActiveDeliveryPeriod,
      selectCurrentlyActiveDeliveryShopKey,
      (usedInventories, deliveryPeriod, shopKey) => {
        return getInventoriesFilteredByShopPeriod(usedInventories, {
          deliveryShopId: checkAcrossShops ? '' : shopKey ?? '',
          deliveryPeriod: deliveryPeriod ?? 'NOW',
        });
      },
    ),
);

export const selectUsedTrueDemandTotal = createSelector(
  selectUsedInventories,
  (usedInventories) => {
    let total = 0;
    Object.keys(usedInventories).forEach((shopId) => {
      Object.keys(usedInventories[shopId]).forEach((period) => {
        const usedInventoriesInPeriod = usedInventories[shopId][period];
        Object.keys(usedInventoriesInPeriod).forEach((inventoryItemId) => {
          if (isTrueDemandInventory(inventoryItemId)) {
            total += usedInventoriesInPeriod[inventoryItemId];
          }
        });
      });
    });

    return total;
  },
);

export type PrepackView = Unpacked<
  ReturnType<typeof selectPrepacksForCurrentDeliveryPeriod>
>;

export const selectPrepacksForCurrentDeliveryPeriod = createSelector(
  selectPrepacks,
  selectSizeEntities,
  selectVariantEntities,
  selectCurrentlyActiveOrderType,
  selectCurrentlyActiveDeliveryPeriod,
  selectCurrentShopCurrentDeliveryPeriodType,
  selectDeliveryWindowType,
  (
    prepacks,
    sizes,
    variants,
    orderType,
    deliveryPeriod,
    currentDeliveryPeriodType,
    deliveryWindow,
  ) => {
    return prepacks
      .filter((prepack) => {
        return prepack.inventoryItems?.some(
          inventoryFilter(
            orderType,
            mapBulkDeliveryPeriodToFuture(
              deliveryPeriod,
              currentDeliveryPeriodType,
            ),
            currentDeliveryPeriodType,
            deliveryWindow,
          ),
        );
      })
      .map((prepack) => {
        const sizesInPrepack = prepack.quantities
          .map((quantity) => sizes[quantity.productId])
          .filter(isNonNullish);

        const variantsInPrepack = [
          ...sizesInPrepack.reduce((acc, size) => {
            return acc.add(size.variantReference);
          }, new Set<string>()),
        ].map((variantId) => variants[variantId]);

        return {
          ...prepack,
          sizes: sizesInPrepack,
          variants: variantsInPrepack,
        };
      });
  },
);

/**
 * Returns the available inventories for the Products of type Size.
 * Because the state is not persisted immediately, it is necessary to keep an in memory state of used inventories for each delivery period.
 * The used inventory is then deducted from the inventory items of each size.
 * @param checkAcrossShops
 * when set to true - calculates used inventories based on quantities in other delivery periods (needed for advanced MDO)
 * when set to false -  calculates used inventories based on quantities in other delivery periods AND other shops (needed for tabs MDO)
 */
export const selectAvailableInventoriesForCurrentDeliveryPeriod = memoize(
  (checkAcrossShops: boolean) =>
    createSelector(
      selectSizes,
      selectPrepacks,
      selectCurrentlyActiveOrderType,
      selectCurrentlyActiveDeliveryPeriod,
      selectUsedInventoriesForOtherDeliveryPeriods(checkAcrossShops),
      selectCurrentShopCurrentDeliveryPeriodType,
      selectDeliveryWindowType,
      (
        sizes,
        prepacks,
        orderType,
        deliveryPeriod,
        usedInventories,
        currentDeliveryPeriodType,
        deliveryWindow,
      ) => {
        const productInventories: {
          [productId: string]: InventoryItemFragment[];
        } = {};
        const sellableItems = [...sizes, ...prepacks];

        sellableItems.forEach((sellable) => {
          if (!sellable || !sellable.inventoryItems || !orderType) {
            return null;
          }

          const inventoryItems = sellable.inventoryItems
            .filter(
              inventoryFilter(
                orderType,
                mapBulkDeliveryPeriodToFuture(
                  deliveryPeriod,
                  currentDeliveryPeriodType,
                ),
                currentDeliveryPeriodType,
                deliveryWindow,
              ),
            )
            .map((invItem) => ({
              ...invItem,
              quantity: invItem.quantity ?? Number.POSITIVE_INFINITY,
            }));

          const inventoryItemsWithAvailableQuantities = inventoryItems.map(
            (inventory) => {
              const alreadyUsed = usedInventories[inventory.itemId] ?? 0;
              const quantity = inventory.quantity - alreadyUsed;
              return { ...inventory, quantity };
            },
          );

          productInventories[sellable.productId] =
            inventoryItemsWithAvailableQuantities;
        });

        return productInventories;
      },
    ),
);

export const selectUIAvailableInventoriesForCurrentDeliveryPeriod = memoize(
  (checkAcrossShops: boolean) =>
    createSelector(
      selectAvailableInventoriesForCurrentDeliveryPeriod(checkAcrossShops),
      selectCurrentlyActiveDeliveryItems,
      (productInventories /*, activeShopActivePeriodDeliveryItems*/) => {
        return Object.entries(productInventories).reduce(
          (
            productInventoriesAcc,
            [productId, inventoriesWithAvailableQuantities],
          ) => {
            const inventoriesWithoutTrueDemand =
              inventoriesWithAvailableQuantities.filter(
                ({ inventoryId }) => !isTrueDemandInventory(inventoryId),
              );
            const maxAvailableQuantity = inventoriesWithoutTrueDemand.reduce(
              (acc, curr) => acc + (curr.quantity ?? 0),
              0,
            );

            return {
              ...productInventoriesAcc,
              [productId]: {
                totals: maxAvailableQuantity,
                inventories: inventoriesWithoutTrueDemand,
                hasTrueDemandInventory: inventoriesWithAvailableQuantities.some(
                  (inv) => isTrueDemandInventory(inv.inventoryId),
                ),
              },
            };
          },
          {},
        );
      },
    ),
);

export const selectAvailableVariants = createSelector(
  selectVariants,
  selectCurrentlyAvailableSizeIds,
  (variants, availableSizes) =>
    variants.filter(isVariantAvailable(availableSizes)),
);

export const selectUnavailableVariants = createSelector(
  selectVariants,
  selectCurrentlyAvailableSizeIds,
  (variants, availableSizes) =>
    variants.filter((variant) => !isVariantAvailable(availableSizes)(variant)),
);

export const selectAvailableVariantsIds = createSelector(
  selectAvailableVariants,
  (variants) => variants.map((v) => v.productId),
);

export const selectDisabledVariants = createSelector(
  selectUnavailableVariants,
  selectDeliveryWindowType,
  (variants: ProductVariant[], windowType: DeliveryWindowType) => {
    return variants
      .map((v) => ({
        id: v.productId,
        dates:
          v.children?.reduce(
            (acc, curr) => [
              ...acc,
              ...(curr.inventoryItems ?? [])
                .filter((i) => !i.isNotConsumable)
                .filter(
                  (i) =>
                    isInRange(
                      DateTime.fromISO(
                        i.displayFromDateUtc ?? i.availableFromDateUtc,
                      ).toJSDate(),
                      DateTime.fromISO(
                        i.displayToDateUtc ?? i.availableToDateUtc,
                      ).toJSDate(),
                      windowType,
                    ) || i.orderType === OrderType.Now,
                )
                .map((i) => ({
                  date: (() => {
                    const date = i.displayFromDateUtc ?? i.availableFromDateUtc;
                    if (isWeekType(windowType)) return firstDayOfWeekUTC(date);
                    if (isMonthType(windowType))
                      return firstDayOfMonthUTC(date);
                    return date;
                  })(),
                  orderType: i.orderType,
                })),
            ],
            [],
          ) ?? [],
      }))
      .reduce((acc, { id, dates }) => {
        const futureDates = dates
          .filter((date: { orderType: OrderType; date: string }) => {
            return date.orderType !== OrderType.Now;
          })
          .map((date: { orderType: OrderType; date: string }) =>
            DateTime.fromISO(date.date),
          );
        return {
          ...acc,
          [id]: {
            hasNow: !!dates.find(
              (date: { orderType: OrderType }) =>
                date.orderType === OrderType.Now,
            ),
            future: futureDates.length
              ? getDeliveryDate(
                  DateTime.min(...futureDates)
                    .toUTC()
                    .toJSDate(),
                  windowType,
                )
              : '',
          },
        };
      }, {} as { [key: string]: boolean | string });
  },
);

export function isInRange(
  from: Date,
  to: Date,
  deliveryWindow: DeliveryWindowType,
) {
  const now = new Date();
  if (deliveryWindow === DeliveryWindowType.Week) {
    return (
      isInDateRange(firstDayOfNextWeek(now), from, to) ||
      isBefore(firstDayOfNextWeek(now), from)
    );
  }

  return isInDateRange(now, from, to) || isBefore(now, from);
}

export function getDeliveryDate(
  date: Date,
  deliveryWindow: DeliveryWindowType,
) {
  const today = new Date();

  if (deliveryWindow === DeliveryWindowType.Week) {
    return isBefore(date, firstDayOfNextWeek(today))
      ? firstDayOfNextWeekUTC(today)
      : date.toISOString();
  }

  if (deliveryWindow === DeliveryWindowType.Month) {
    return isBefore(date, firstDayOfMonthDate(today))
      ? firstDayOfMonthUTC(today.toISOString())
      : date.toISOString();
  }

  return isBefore(date, today) ? today.toISOString() : date.toISOString();
}

const selectSortedVariantsFactory = (config: {
  includeInitiallySelectedVariantEvenWhenItMightNotBeAvailable: boolean;
}) =>
  createSelector(
    selectVariants,
    selectAvailableVariantsIds,
    selectInitialSelectedVariantId,
    (variants, availableVariantsIds, initialVariantId) =>
      variants
        .filter(
          (variant) =>
            (config.includeInitiallySelectedVariantEvenWhenItMightNotBeAvailable &&
              variant.productId === initialVariantId) ||
            !isNeverAvailableVariant(variant),
        )
        .sort(compareVariants(initialVariantId, availableVariantsIds)),
  );

export const selectSortedVariants = selectSortedVariantsFactory({
  includeInitiallySelectedVariantEvenWhenItMightNotBeAvailable: true,
});

export const selectSortedAvailableVariants = selectSortedVariantsFactory({
  includeInitiallySelectedVariantEvenWhenItMightNotBeAvailable: false,
});

export const selectEnabledVariants = createSelector(
  selectSortedVariants,
  selectDisabledVariants,
  (sortedVariants, disabledVariants) =>
    sortedVariants.filter((variant) => !disabledVariants[variant.productId]),
);

export const selectAvailableInventoriesForSelectedSizeForSelectedOrderType =
  createSelector(
    selectCurrentlySelectedSize,
    selectCurrentlyActiveOrderType,
    (size, orderType) => {
      return (size?.inventoryItems ?? []).filter(
        (inventory) => inventory.orderType === orderType,
      );
    },
  );

export const selectCurrentlyActiveDeliveryItemsForSelectedSize = createSelector(
  selectCurrentlySelectedSizeId,
  selectCurrentlyActiveDeliveryItems,
  (sizeId, deliveryItems) => {
    const defaultQuantity = {
      quantity: 0,
      usedTrueDemandQuantity: 0,
    };
    if (sizeId === null) {
      return defaultQuantity;
    }

    return deliveryItems.items[sizeId] ?? defaultQuantity;
  },
);

export const selectNotConsumableInventoriesForSelectedSizeForSelectedOrderType =
  createSelector(
    selectCurrentlySelectedSize,
    selectCurrentlyActiveOrderType,
    (size, orderType) => {
      return (size?.isNotConsumableInventories ?? []).filter(
        (inventory) => inventory.orderType === orderType,
      );
    },
  );

export const selectAvailableInventoriesForSelectedSizeForOrderType =
  createSelector(
    selectAvailableInventoriesForSelectedSizeForSelectedOrderType,
    selectSplitInventoriesOverviewForSameDateEnabled,
    availableInventoriesForSelectedSizeForOrderType,
  );

// we assume that we can consume all available bulk and each consignment inventory should return actual number (not 0 or Infinity)
export const selectBulkTotalQuantityForSelectedSizeForOrderType =
  createSelector(
    selectAvailableInventoriesForSelectedSizeForSelectedOrderType,
    (inventories) =>
      inventories
        .filter((item) => item.isConsignment)
        .map((item) => item.quantity)
        .reduce((acc, qty) => (acc ?? 0) + (qty ?? 0), 0) ?? 0,
  );

// Inventories where first inventory holds bulk + normal availability
export const selectAvailableInventoriesQuantitiesForDeliveryPeriod =
  createSelector(
    selectAvailableInventoriesForSelectedSizeForOrderType,
    selectSplitInventoriesOverviewForSameDateEnabled,
    availableInventoriesQuantitiesForDeliveryPeriod,
  );

// we subtract bulk availability from the first inventory that supposed to present Now inventory
export const selectInventoryOverviewsWithoutBulk = createSelector(
  selectAvailableInventoriesQuantitiesForDeliveryPeriod,
  selectBulkTotalQuantityForSelectedSizeForOrderType,
  (inventories, bulk) =>
    inventories
      .filter(({ inventoryId }) => !isTrueDemandInventory(inventoryId))
      .map((inventory, index) => ({
        ...inventory,
        maxQuantity:
          index === 0 ? inventory.maxQuantity - bulk : inventory.maxQuantity,
      }))
      .filter(({ maxQuantity }) => maxQuantity > 0),
);

export const selectNotConsumedInventoryOverviews = createSelector(
  selectNotConsumableInventoriesForSelectedSizeForSelectedOrderType,
  selectSplitInventoriesOverviewForSameDateEnabled,
  (inventories, isSplitEnabled) => {
    return availableInventoriesQuantitiesForDeliveryPeriod(
      availableInventoriesForSelectedSizeForOrderType(
        inventories,
        isSplitEnabled,
      ),
      isSplitEnabled,
    );
  },
);

export const selectIsNoInventoriesAvailable = createSelector(
  selectAvailableInventoriesQuantitiesForDeliveryPeriod,
  selectBulkTotalQuantityForSelectedSizeForOrderType,
  (inventories, bulk) => !inventories.length && bulk === 0,
);

const selectShowPrepackPrices = selectConfigurationValueFrom<
  ConfigStripConfigurationKeys.ShowPrepackPrices,
  ConfigStripConfigurations
>(
  CONFIG_STRIP_FEATURE,
  ConfigStripConfigurationKeys.ShowPrepackPrices,
  configStripConfigurationDefaultValues[
    ConfigStripConfigurationKeys.ShowPrepackPrices
  ],
);

export const selectPricesForCurrentSelection = createSelector(
  selectPrices,
  selectProductId,
  selectCurrentlySelectedVariantId,
  selectCurrentlySelectedSizeId,
  selectCurrentlySelectedPrepackId,
  selectCurrentlyActiveDeliveryPeriod,
  selectCurrentlyActiveOrderType,
  selectDisplayByMode,
  selectShowPrepackPrices,
  (
    productPrices,
    articleId,
    variantId,
    sizeId,
    prepackId,
    deliveryPeriod,
    orderType,
    displayByMode,
    showPrepackPrices,
  ) =>
    getProductPrices({
      productPrices: productPrices[getPriceType(orderType)],
      articleId,
      variantId,
      sizeId,
      deliveryPeriod,
      preSelectedOrderType: orderType,
      prepackId,
      displayByMode,
      showPrepackPrices,
    }),
);

const selectPricesWspCurrency = createSelector(
  selectPricesForCurrentSelection,
  (prices) => {
    return prices.wsp?.currency ?? null;
  },
);

const selectPricesRrpCurrency = createSelector(
  selectPricesForCurrentSelection,
  (prices) => {
    return prices.rrp?.currency ?? null;
  },
);

const selectPricesDisplayedCurrency = createSelector(
  selectShowWholeSalePrices,
  selectPricesWspCurrency,
  selectPricesRrpCurrency,
  (showWsp, currencyWsp, currencyRrp) => {
    return showWsp ? currencyWsp : currencyRrp;
  },
);

export const selectTotals = createSelector(
  selectTotalQuantity,
  selectTotalPrices,
  selectPricesDisplayedCurrency,
  (quantity, prices, currency) => ({
    quantity,
    prices,
    currency,
  }),
);

export const selectAvailableVariantsPrices = createSelector(
  selectPrices,
  selectProductId,
  selectAvailableVariants,
  selectCurrentlyActiveDeliveryPeriod,
  selectCurrentlyActiveOrderType,
  (productPrices, articleId, availableVariants, deliveryPeriod, orderType) => {
    const variantsPrices: ActiveProductPricesDic = {};
    availableVariants.forEach((variant) => {
      variantsPrices[variant.productId] = getProductPrices({
        productPrices: productPrices[getPriceType(orderType)],
        articleId,
        variantId: variant.productId,
        sizeId: null,
        deliveryPeriod,
        preSelectedOrderType: orderType,
      });
    });
    return variantsPrices;
  },
);

export const selectShowSizeGrid = createSelector(
  selectAvailableVariants,
  selectPrepacksForCurrentDeliveryPeriod,
  selectDisplayByMode,
  (availableVariants, availableprepacks, displayByMode) => {
    const hasLooseSizes = !!availableVariants?.length;
    const hasPrepacks = !!availableprepacks?.length;

    return (
      ((displayByMode === DisplayByMode.Size ||
        displayByMode === DisplayByMode.AdvancedSize) &&
        hasLooseSizes) ||
      (!hasPrepacks && hasLooseSizes)
    );
  },
);

export const selectMemoProductSizes = createSelector(
  selectCachedState,
  (
    state: CachedState,
    propsFn: () => { articleId: string; brandId: string },
  ): MemoProductSizes | null => {
    const { articleId, brandId } = propsFn();
    return state.memoProductSizes?.[`${brandId} ${articleId}`] ?? null;
  },
);

export const selectMemoProductSizesV2 = (payload: {
  articleId: string;
  brandId: string;
}) =>
  createSelector(
    selectCachedState,
    (state: CachedState): MemoProductSizes | null => {
      const { articleId, brandId } = payload;
      return state.memoProductSizes?.[`${brandId} ${articleId}`] ?? null;
    },
  );

export const selectActiveDeliveryShops = createSelector(
  selectBusinessConfigStripState,
  selectDataConfigStripState,
  (business, data) =>
    business.activeDeliveryShops.length
      ? business.activeDeliveryShops
      : data.deliveryShops ?? [],
);

export const selectDisplayDeliveryShopData = createSelector(
  selectDeliveryShops,
  selectCurrentlyActiveDeliveryShopKey,
  (shops, activeShopKey) => {
    return (shops ?? []).find(
      (shop) => getDeliveryShopId(shop) === activeShopKey,
    );
  },
);

export const selectActiveVariantInventories = createSelector(
  selectCurrentlySelectedVariant,
  (variant) => variant?.children?.flatMap((size) => size.inventoryItems) ?? [],
);

export const selectHasPrepacksForCurrentDeliveryPeriod = createSelector(
  selectPrepacksForCurrentDeliveryPeriod,
  (prepacks) => Boolean(prepacks?.length),
);

export const selectShowAMDO = createSelector(
  selectAdvancedMDOAvailableAndEnabled,
  selectDeliveryShopsCount,
  (availableAndEnabled, shopsCount) => availableAndEnabled && shopsCount > 1,
);

/**
 * Select available inventories
 * @param useGlobalInventories
 * when param is "true", it returns inventories of all sizes and prepacks of the product
 * when param is "false", it returns inventories of sizes of the currently selected variant of the product
 */
export const selectAvailableInventories = (useGlobalInventories: boolean) =>
  createSelector(
    selectSizesInventories,
    selectPrepackInventories,
    selectActiveVariantInventories,
    (sizesInventories, prepackInventories, activeVariantInventories) => {
      return (
        useGlobalInventories
          ? [...sizesInventories, ...prepackInventories]
          : [...activeVariantInventories]
      )
        .filter(isNonNullish)
        .filter((inventory) => inventory.quantity !== 0);
    },
  );

export const selectCanPersist = createSelector(
  selectPersistingChanges,
  selectTotals,
  selectIsUpdatingCart,
  selectDirty,
  selectValidating,
  selectIsDisabledOnBasketUpdate,
  (
    isPersisting,
    totals,
    isUpdatingCart,
    isDirty,
    isValidating,
    isDisabledOnBasketUpdate,
  ) => {
    const totalQuantity = totals?.quantity ?? 0;
    return !(
      isValidating ||
      isPersisting ||
      isDisabledOnBasketUpdate ||
      // more then 0 and user made any change, if he not then it's update
      (totalQuantity > 0 && !isDirty) ||
      // disable when quantity is 0 but it's not an update of a basket
      (totalQuantity === 0 && !isUpdatingCart)
    );
  },
);
